import { useDispatch, useReduxState } from 'hooks';
import isEmail from 'validator/lib/isEmail';
import api from 'api';
import {
  attributesConditionsMatch,
  canUserCustomAttributeBeEdited,
  canUserCustomAttributeBeViewed,
  getApplicableUserCustomAttributes
} from 'shared-helpers/customAttributes';
import {
  decodeCustomAttributeKeyForForm,
  encodeCustomAttributeKeyForForm,
  unsetRemovedUserCustomAttributes
} from 'lib/customAttributes';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';
import { UserCustomAttribute } from 'components/user';

import type { RequestUser, User, UserCustomAttributes } from 'shared-types';
import type { PageState } from 'types';

type FormValues = Pick<User, 'customAttributes' | 'email' | 'username'>;

interface Props {
  loggedInUserHasEditPermissions: boolean;
  targetUser: RequestUser;
}

const ProfileInfoEdit = ({
  loggedInUserHasEditPermissions,
  targetUser
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig, user: loggedInUser } = useReduxState();

  const {
    accountEnableUsernameAccounts,
    userCustomAttributes,
    userUseName,
    userUseNameInCertificate
  } = serverConfig;

  const handleSubmit = async (values: FormValues) => {
    if (targetUser.email && !values.email && accountEnableUsernameAccounts) {
      values.email = null;
    }

    // include email only if authenticator is local
    if (!['local', 'local-totp'].includes(targetUser.authenticatorType)) {
      delete values.email;
    }

    if (values.customAttributes) {
      const decodedCustomAttributes = Object.fromEntries(
        Object.entries(values.customAttributes).map(([key, value]) => [
          decodeCustomAttributeKeyForForm(key),
          value
        ])
      );

      const customAttributes = unsetRemovedUserCustomAttributes({
        reqUser: loggedInUser,
        userCustomAttributes,
        userCustomAttributeValues: decodedCustomAttributes
      });

      values.customAttributes = {
        ...targetUser.customAttributes,
        ...customAttributes
      };
    }

    // call api
    const response = await api.post('/auth/profile', values);

    // handle response
    if (response.data && response.data.success) {
      const updatedSelf = await dispatch.whoAmI();

      dispatch.updatePageState({ user: updatedSelf as PageState['user'] });
      dispatch.addNotice({
        text: messages.profileEditProfileSuccess,
        type: 'success'
      });
    } else if (response.data?.error === 'invalid_current_password') {
      await dispatch.handleErrorDispatch(
        null,
        messages.authCurrentPasswordInvalid
      );
      return false;
    } else if (response.data?.error === 'username_as_email') {
      await dispatch.handleErrorDispatch(
        null,
        messages.authInvalidEmailAsUsername
      );
      return false;
    } else if (
      response.data?.error === 'invalid_custom_attribute_required_password'
    ) {
      await dispatch.handleErrorDispatch(
        null,
        messages.authRequiredPasswordInvalid
      );
      return false;
    } else {
      throw new Error('error');
    }
  };

  const validateNotEmpty = (value) => {
    return !(value && value.toString().trim().length)
      ? messages.commonFieldRequired
      : undefined;
  };

  const validateEmail = async (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value && accountEnableUsernameAccounts) return undefined;
    if (!value) return messages.authEmailEmptyError;
    if (!isEmail(value, { domain_specific_validation: true }))
      return messages.usersInvalidEmail;

    try {
      await api.head(
        `/admin/users?email=${encodeURIComponent(value)}&id=ne|${targetUser.id}`
      );
      return messages.authEmailInUseError;
    } catch (err) {
      return undefined;
    }
  };

  const validateUsername = async (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value) return messages.authUsernameEmptyError;
    if (isEmail(value)) return messages.authInvalidEmailAsUsername;

    try {
      await api.head(`/admin/users?username=${value}&id=ne|${targetUser.id}`);
      return messages.authUsernameInUseError;
    } catch (err) {
      return undefined;
    }
  };

  const initialValues: Partial<
    Pick<
      User,
      'email' | 'firstName' | 'lastName' | 'username' | 'customAttributes'
    >
  > = targetUser
    ? {
        email: targetUser.email,
        firstName: targetUser.firstName,
        lastName: targetUser.lastName,
        username: targetUser.username
      }
    : {};

  const applicableUserCustomAttributes = getApplicableUserCustomAttributes(
    targetUser,
    userCustomAttributes
  ).filter((attribute) =>
    canUserCustomAttributeBeViewed({
      allowUserPermissionForAdmin: true,
      user: loggedInUser,
      userCustomAttribute: attribute
    })
  );

  initialValues.customAttributes = {};
  for (const key in targetUser.customAttributes) {
    const attr = applicableUserCustomAttributes.find(
      (attr) => attr.key === key
    );
    if (attr) {
      initialValues.customAttributes[encodeCustomAttributeKeyForForm(key)] =
        targetUser.customAttributes[key];
    }
  }

  const userCanEditBasicInfo =
    loggedInUserHasEditPermissions ||
    !serverConfig.accountPreventUserEditingProfileInfo;

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonIcon="pen"
      buttonProps={{
        iconStyle: {
          fontSize: '0.9rem',
          marginRight: 3,
          position: 'relative',
          top: -2
        }
      }}
      buttonSize="small"
      buttonStyle={{
        borderRadius: 30,
        marginLeft: 8,
        padding: '3px 10px 4px 8px',
        position: 'relative',
        top: -1
      }}
      buttonText={messages.commonEdit}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSaveChanges}
      modalTitle={messages.profileEditProfile}
      modalWidth="medium"
      onSubmit={handleSubmit}
    >
      {(formState) => (
        <div>
          <FormGroup
            htmlFor="edit-profile-email"
            label={messages.commonEmail}
            maxWidth="320px"
          >
            <Text
              customValidateAsync={validateEmail}
              disabled={
                !['local', 'local-totp'].includes(
                  targetUser?.authenticatorType
                ) || !userCanEditBasicInfo
              }
              field="email"
              id="edit-profile-email"
            />
          </FormGroup>

          {!userUseName || accountEnableUsernameAccounts ? (
            <FormGroup
              htmlFor="edit-profile-username"
              label={messages.commonUsername}
              maxWidth="320px"
            >
              <Text
                customValidateAsync={validateUsername}
                disabled={!userCanEditBasicInfo}
                field="username"
                id="edit-profile-username"
              />
            </FormGroup>
          ) : null}

          {userUseName || userUseNameInCertificate ? (
            <FormGroup>
              <div className="row-md">
                <div className="col-1of2">
                  <FormGroup
                    htmlFor="edit-profile-first-name"
                    label={messages.usersFirstName}
                    maxWidth="320px"
                  >
                    <Text
                      disabled={!userCanEditBasicInfo}
                      field="firstName"
                      id="edit-profile-first-name"
                      validate={validateNotEmpty}
                      validateOnChange={true}
                    />
                  </FormGroup>
                </div>
                <div className="col-1of2">
                  <FormGroup
                    htmlFor="edit-profile-last-name"
                    label={messages.usersLastName}
                    maxWidth="320px"
                  >
                    <Text
                      disabled={!userCanEditBasicInfo}
                      field="lastName"
                      id="edit-profile-last-name"
                      validate={validateNotEmpty}
                      validateOnChange={true}
                    />
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
          ) : null}

          {applicableUserCustomAttributes.length
            ? applicableUserCustomAttributes.map((attribute, i) => {
                const customAttributes: UserCustomAttributes = {
                  ...targetUser.customAttributes,
                  ...(Object.fromEntries(
                    Object.entries(
                      formState.values?.customAttributes || {}
                    ).map(([key, value]) => [
                      decodeCustomAttributeKeyForForm(key),
                      value
                    ])
                  ) as UserCustomAttributes)
                };

                const conditionsMatch = attributesConditionsMatch({
                  conditions: attribute.conditions,
                  customAttributeValues: customAttributes,
                  user: targetUser
                });
                if (!conditionsMatch) return null;

                const isReadOnly = !canUserCustomAttributeBeEdited({
                  allowUserPermissionForAdmin: true,
                  user: targetUser,
                  userCustomAttribute: attribute
                });

                const isRequired = attribute.isRequired?.value
                  ? attributesConditionsMatch({
                      conditions: attribute.isRequired?.conditions || [],
                      customAttributeValues: customAttributes,
                      isRequiredCheck: true,
                      user: targetUser
                    })
                  : false;

                const error =
                  formState.errors.customAttributes &&
                  formState.errors.customAttributes[
                    encodeCustomAttributeKeyForForm(attribute.key)
                  ];

                return (
                  <UserCustomAttribute
                    attribute={attribute}
                    calledFrom="profile"
                    customAttributeOriginalValues={targetUser.customAttributes}
                    customAttributeValues={{
                      ...targetUser.customAttributes,
                      ...formState.values?.customAttributes
                    }}
                    error={error}
                    id={i}
                    isAttributeRequired={isRequired}
                    isReadOnly={isReadOnly}
                    key={`${attribute.key}-${i}-${isRequired}`}
                    maxWidth={attribute.type === 'String' ? '320px' : null}
                  />
                );
              })
            : null}

          {['local', 'local-totp'].includes(targetUser?.authenticatorType) ? (
            <FormGroup
              htmlFor="edit-profile-current-password"
              label={messages.authCurrentPassword}
              maxWidth="320px"
              style={{ marginTop: 40 }}
            >
              <Text
                field="currentPassword"
                id="edit-profile-current-password"
                type="password"
                validate={validateNotEmpty}
                validateOnChange={true}
              />
            </FormGroup>
          ) : null}
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default ProfileInfoEdit;
